import React, { useEffect, useState } from 'react';
import { Outlet, useNavigate, useLocation, Link } from 'react-router-dom';
import {
  Box,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Typography,
  Avatar,
  IconButton,
  Menu,
  MenuItem,
  Divider,
  Tooltip,
} from '@mui/material';
import { useTheme, styled } from '@mui/material/styles';
import { useAuthStore } from '../../store/auth.store';
import { useTaskStore } from '../../store/task.store';
import { useEmployeeStore } from '../../store/employee.store';
import { useTeamStore } from '../../store/team.store';
import { useConfig } from '../../providers/ConfigProvider';
import { useTranslation } from 'react-i18next';
import i18n from '../../i18n/config';
import {
  Home2,
  People,
  Profile,
  ClipboardTick,
  TaskSquare,
  Flag,
  Setting2,
  LogoutCurve,
  HambergerMenu,
  Global,
  Cpu,
} from 'iconsax-react';

const DRAWER_WIDTH = 240;
const DRAWER_COLLAPSED_WIDTH = 72;

const StyledDrawer = styled(Drawer)(({ theme }) => ({
  '& .MuiDrawer-paper': {
    backgroundColor: '#1C1F2E',
    color: '#E2E8F0',
    borderRight: 'none',
    boxSizing: 'border-box',
    overflowX: 'hidden',
    boxShadow: '2px 0 16px rgba(0,0,0,0.12)',
    transition: theme.transitions.create('width', {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.enteringScreen,
    }),
  },
}));

const NavItem = styled(ListItemButton, {
  shouldForwardProp: (prop) => prop !== 'active',
})<{ active?: string; component?: React.ElementType; to?: string }>(({ active }) => ({
  borderRadius: 10,
  margin: '2px 8px',
  padding: '10px 12px',
  gap: 12,
  transition: 'all 0.15s ease',
  backgroundColor: active === 'true' ? 'rgba(99, 102, 241, 0.18)' : 'transparent',
  color: active === 'true' ? '#A5B4FC' : '#9CA3AF',
  '&:hover': {
    backgroundColor:
      active === 'true' ? 'rgba(99, 102, 241, 0.22)' : 'rgba(255,255,255,0.07)',
    color: active === 'true' ? '#A5B4FC' : '#D1D5DB',
  },
}));

const navItems = [
  { key: 'overview', label: 'layout.overview', icon: Home2, path: '/dashboard' },
  { key: 'my-tasks', label: 'layout.myTasks', icon: ClipboardTick, path: '/dashboard/my-tasks' },
  { key: 'tasks', label: 'layout.tasks', icon: TaskSquare, path: '/dashboard/tasks' },
  { key: 'objectives', label: 'layout.objectives', icon: Flag, path: '/dashboard/objectives' },
  { key: 'teams', label: 'layout.teams', icon: People, path: '/dashboard/teams' },
  { key: 'members', label: 'layout.members', icon: Profile, path: '/dashboard/members' },
  { key: 'seqai', label: 'layout.seqai', icon: Cpu, path: '/dashboard/seqai' },
  { key: 'settings', label: 'layout.settings', icon: Setting2, path: '/dashboard/settings' },
];

export default function DashboardLayout() {
  const theme = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuthStore();
  const { t } = useTranslation();
  const { updateDirection } = useConfig();
  const [collapsed, setCollapsed] = useState(false);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  useEffect(() => {
    const tasks = useTaskStore.getState();
    if (!tasks.tasksFetchedAt) tasks.fetchTasks();
    if (!tasks.myTasksFetchedAt) tasks.fetchMyTasks();
    if (useEmployeeStore.getState().employees.length === 0) useEmployeeStore.getState().fetchEmployees();
    if (useTeamStore.getState().teams.length === 0) useTeamStore.getState().fetchTeams();
  }, []);

  const drawerWidth = collapsed ? DRAWER_COLLAPSED_WIDTH : DRAWER_WIDTH;

  const handleLogout = () => {
    setAnchorEl(null);
    logout();
    navigate('/login');
  };

  const isActive = (path: string) => {
    if (path === '/dashboard') return location.pathname === '/dashboard';
    if (path === '/dashboard/tasks') {
      return location.pathname === '/dashboard/tasks' || location.pathname.startsWith('/dashboard/tasks/');
    }
    if (path === '/dashboard/objectives') {
      return location.pathname === '/dashboard/objectives' || location.pathname.startsWith('/dashboard/objectives/');
    }
    return location.pathname === path || location.pathname.startsWith(`${path}/`);
  };

  const isArabic = i18n.language === 'ar';
  const toggleLanguage = () => {
    const nextLang = isArabic ? 'en' : 'ar';
    i18n.changeLanguage(nextLang);
    localStorage.setItem('i18nextLng', nextLang);
    updateDirection(nextLang === 'ar' ? 'rtl' : 'ltr');
  };

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh', bgcolor: 'background.default' }}>
      <StyledDrawer
        variant="permanent"
        sx={{ width: drawerWidth, flexShrink: 0, '& .MuiDrawer-paper': { width: drawerWidth } }}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>

          {/* Header — logo + hamburger (same as Mohasby) */}
          <Box sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: collapsed ? 'center' : 'space-between',
            px: collapsed ? 0 : 2,
            pt: 2.5,
            pb: collapsed ? 1.5 : 2,
            gap: 1,
          }}>
            {!collapsed && (
              <img
                src="/WorkPilot_Logo.png"
                alt="WorkPilot"
                style={{ height: 38, width: 'auto', objectFit: 'contain' }}
              />
            )}
            <Tooltip title={collapsed ? t('layout.expand') : t('layout.collapse')} placement="right">
              <IconButton
                onClick={() => setCollapsed(c => !c)}
                size="small"
                sx={{
                  color: '#9CA3AF',
                  flexShrink: 0,
                  '&:hover': { bgcolor: 'rgba(255,255,255,0.07)', color: '#D1D5DB' },
                }}
              >
                <HambergerMenu size={20} color="currentColor" />
              </IconButton>
            </Tooltip>
          </Box>

          <List sx={{ flex: 1, pt: 0.5 }}>
            {navItems.map(item => {
              const Icon = item.icon;
              const active = isActive(item.path);
              return (
                <ListItem key={item.key} disablePadding>
                  <Tooltip title={collapsed ? t(item.label) : ''} placement="right">
                    <NavItem
                      component={Link}
                      to={item.path}
                      active={active ? 'true' : 'false'}
                    >
                      <ListItemIcon sx={{ minWidth: 0, color: 'inherit' }}>
                        <Icon
                          size={20}
                          variant={active ? 'Bold' : 'Linear'}
                          color={active ? '#A5B4FC' : '#6B7280'}
                        />
                      </ListItemIcon>
                      {!collapsed && (
                        <ListItemText
                          primary={t(item.label)}
                          primaryTypographyProps={{
                            fontSize: '0.875rem',
                            fontWeight: active ? 600 : 400,
                            color: 'inherit',
                          }}
                        />
                      )}
                    </NavItem>
                  </Tooltip>
                </ListItem>
              );
            })}
          </List>

          <Divider sx={{ borderColor: 'rgba(255,255,255,0.06)', mx: 2 }} />

          {/* Language + Logout — same style as nav items */}
          <List dense sx={{ py: 1 }}>
            <ListItem disablePadding>
              <Tooltip title={collapsed ? (isArabic ? 'Switch to English' : 'التبديل للعربية') : ''} placement="right">
                <ListItemButton
                  onClick={toggleLanguage}
                  sx={{
                    borderRadius: 10, margin: '2px 8px', padding: '10px 12px', gap: '12px',
                    color: '#9CA3AF',
                    '&:hover': { bgcolor: 'rgba(255,255,255,0.07)', color: '#D1D5DB' },
                  }}
                >
                  <ListItemIcon sx={{ minWidth: 0 }}>
                    <Global size={20} color="#6B7280" />
                  </ListItemIcon>
                  {!collapsed && (
                    <ListItemText
                      primary={isArabic ? 'English' : 'عربي'}
                      primaryTypographyProps={{ fontSize: '0.875rem', color: '#9CA3AF' }}
                    />
                  )}
                </ListItemButton>
              </Tooltip>
            </ListItem>
            <ListItem disablePadding>
              <Tooltip title={collapsed ? t('auth.logout') : ''} placement="right">
                <ListItemButton
                  onClick={handleLogout}
                  sx={{
                    borderRadius: 10, margin: '2px 8px', padding: '10px 12px', gap: '12px',
                    color: '#9CA3AF',
                    '&:hover': { bgcolor: 'rgba(239,68,68,0.1)', color: '#FCA5A5' },
                  }}
                >
                  <ListItemIcon sx={{ minWidth: 0 }}>
                    <LogoutCurve size={20} color="#6B7280" />
                  </ListItemIcon>
                  {!collapsed && (
                    <ListItemText
                      primary={t('auth.logout')}
                      primaryTypographyProps={{ fontSize: '0.875rem', color: '#9CA3AF' }}
                    />
                  )}
                </ListItemButton>
              </Tooltip>
            </ListItem>
          </List>

          <Divider sx={{ borderColor: 'rgba(255,255,255,0.06)', mx: 2, mb: 0.5 }} />

          <Box
            sx={{
              p: collapsed ? '12px 8px' : '12px 12px',
              display: 'flex',
              alignItems: 'center',
              gap: 1.5,
              cursor: 'pointer',
              borderRadius: 2,
              mx: 1,
              mb: 1,
              '&:hover': { bgcolor: 'rgba(255,255,255,0.06)' },
            }}
            onClick={e => setAnchorEl(e.currentTarget)}
          >
            <Avatar
              sx={{
                width: 36,
                height: 36,
                background: 'linear-gradient(135deg, #3B82F6, #4F46E5)',
                fontSize: '0.875rem',
                fontWeight: 600,
                flexShrink: 0,
              }}
            >
              {user?.name?.charAt(0).toUpperCase()}
            </Avatar>
            {!collapsed && (
              <Box sx={{ overflow: 'hidden', flex: 1 }}>
                <Typography noWrap sx={{ fontSize: '0.8125rem', fontWeight: 600, color: '#E2E8F0' }}>
                  {user?.name}
                </Typography>
                <Typography sx={{ fontSize: '0.75rem', color: '#64748B', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {user?.email}
                </Typography>
              </Box>
            )}
          </Box>
        </Box>
      </StyledDrawer>

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={() => setAnchorEl(null)}
        transformOrigin={{ horizontal: 'left', vertical: 'bottom' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
        PaperProps={{
          sx: {
            mt: -1,
            ml: 1,
            minWidth: 180,
            borderRadius: 2,
            boxShadow: '0 10px 40px rgba(0,0,0,0.12)',
            border: '1px solid #F1F5F9',
          },
        }}
      >
        <MenuItem onClick={() => { setAnchorEl(null); navigate('/dashboard/settings'); }} sx={{ fontSize: '0.875rem', gap: 1.5 }}>
          <Setting2 size={16} /> {t('layout.settings')}
        </MenuItem>
      </Menu>

      <Box
        component="main"
        sx={{
          flex: 1,
          minWidth: 0,
          overflow: 'auto',
          p: 3,
          transition: theme.transitions.create('margin', {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.enteringScreen,
          }),
        }}
      >
        <Outlet />
      </Box>
    </Box>
  );
}
